import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useImages } from '@dyl/systems/ImagesAssetsManager';
import DataGridDyl from '@dyl/components/DataGridDyl';
import ImageMiniPreview from '@dyl/components/ImageMiniPreview';
import { Visibility as ShowIcon, VisibilityOff as HideIcon } from '@mui/icons-material';
import { Button, Box, Typography } from '@mui/material';
import useStyles from './styles';
import { property } from 'lodash';
import useLogic from './logic';
import { CollapsibleButton } from '@dyl/components/CollapsibleButtonGroup';
import { CollapsibleAssetButtonGroup } from '@dyl/components/CollapsibleButtonGroup';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import { useSettings } from '@dyl/systems/SettingsManager';
import deleteAssetImage from "@dyl/apiClients/backendApi/deleteAssetImage";
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import SearchField from '@dyl/components/SearchField';

const getRowId = property('image_id');

/**
 * @param {Parameters<DataGridDyl>[0]} props
 */
export default function ImagesList(props) {
  const classes = useStyles();
  const { shouldShowImagePreviewsByDefault } = useSettings();

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeout = useRef();

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 400);
    return () => clearTimeout(debounceTimeout.current);
  }, [searchValue]);
  const [sortModel, setSortModel] = useState([{ field: 'image_id', sort: 'asc' }]);
  const { images, countFiltered, imagesStatus, refreshImages } = useImages( page, '', debouncedSearch, pageSize, sortModel);
  const [shouldShowPreviews, setShowPreviews] = useState(shouldShowImagePreviewsByDefault);

  const {
    selectedIds, setSelectedIds,
    selectAll,
    isExporting, onExportClick,
  } = useLogic('assets_images');

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const columns = useMemo(() => [
    {
      headerName: 'Name', field: 'image_id', flex: 4,
      renderCell({ value }) {
        return (
          <>
            {shouldShowPreviews && (
              <ImageMiniPreview imageId={value} style={{ marginRight: 8 }} />
            )}
            {value}
          </>
        );
      },
    },
    {
      headerName: 'Updated Date/Time', field: 'updated_at', flex: 2,
      valueFormatter({ value }) {
        return quickDateFormat(value);
      }
    },
    {
      headerName: 'Delete', field: 'ADMIN_DELETE', flex: 2,
      renderCell({ row }) {
        return (<GenericDeleteAction
          data={row}
          promptTexts={{
            dialogTitle: () => 'Delete Image?',
            dialogText: (data) => `Are you sure you want to delete image "${data.image_id}"?`,
            ongoingNotification: (data) => `Deleting image "${data.image_id}"...`,
            successNotification: (data, response) => `Deleted image "${data.image_id}".`,
            failureNotification: (data, response) =>
              `The image you are trying to delete is associated with the following task(s): ` +
              `${response.associated_tasks.map(x => x.task_id_name).join(', ')}, please remove the asset from the associated task(s) in order to delete it.`,
            errorNotification: () => `Failed to delete image.`
          }}
          deleteAction={(data) => deleteAssetImage(data.image_id)}
          responseSuccessPredicate={(response) => response.success}
          onDelete={() => refreshImages()} />)
      }
    },
  ], [refreshImages, shouldShowPreviews]);

  return (
    <>
      <SearchField
        value={searchValue}
        onChange={(e) => {
          const val = e.target.value;
          setSearchValue(val);
        }}
        placeholder="Search..."
      />
      <Box className={classes.actionLine}>
        <Typography variant='h6'>
          Showing {images.length} of {countFiltered} records.
        </Typography>
        <CollapsibleAssetButtonGroup className={classes.actionGroup} color='primary'>
          <CollapsibleButton down='md' onClick={() => onExportClick(selectedIds)} disabled={isExporting}>
            Export Asset(s)
          </CollapsibleButton>
        </CollapsibleAssetButtonGroup>
        <Button onClick={() => setShowPreviews(prev => !prev)} color='primary'>
        {shouldShowPreviews ? <HideIcon /> : <ShowIcon />}
        {shouldShowPreviews ? 'Hide Previews' : 'Show Previews'}
      </Button>
      </Box>

   <DataGridDyl
      rows={images}
      getRowId={getRowId}
      columns={columns}
      loading={imagesStatus !== 'complete'}
      selectionType='checkboxes'
      selectionModel={selectedIds}
      onSelectionModelChange={(newSelection) => {
      setSelectedIds((prev) => {
        const currentPageIds = images.map((img) => img.image_id);
        const added = newSelection.filter((id) => !prev.includes(id));
        const removed = prev.filter(
          (id) => currentPageIds.includes(id) && !newSelection.includes(id)
        );
          return [...prev.filter((id) => !removed.includes(id)), ...added];
        });
      }}
      page={page}
      onPageChange={handlePageChange}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      rowCount={countFiltered}
      paginationMode='server'
      sortingMode='server'
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      overrideSelectAll={selectAll}
      disableColumnMenu={true}
      pagination
      {...props}
    />
    </>
  );
}
